/**
 * Responder a um pedido de horário.
 *
 * O barbeiro não decide olhando o pedido isolado — ele decide olhando o que já
 * tem antes e depois. Por isso a agenda ao redor vem junto, com o pedido
 * encaixado na posição em que ele está sendo colocado: mover quinze minutos
 * para trás só faz sentido se der para ver o que está lá atrás.
 *
 * Confirmar, ajustar e escrever o recado acontecem numa operação só. Se fossem
 * passos separados, o cliente receberia dois avisos para uma decisão — e o
 * segundo pareceria arrependimento.
 */

import type {
  AppointmentView,
  AvailabilityServiceView,
  DayAgendaView,
  ServiceView,
} from '@barbearia/shared';
import { describeServices, shortServicesLabel } from '@barbearia/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Badge, Button, ErrorNote, OptionList, Sheet, Spinner } from '../../components/ui.tsx';
import { ApiError, api } from '../../lib/api.ts';
import { useTenant } from '../../lib/contexts.tsx';
import { duration, longDateOf, minutesOfDay, parseHhMm, timeOf } from '../../lib/format.ts';

interface Props {
  request: AppointmentView;
  onClose: () => void;
}

/**
 * Os nomes dos serviços escolhidos agora.
 *
 * O pedido é a primeira fonte — é o que já está na tela, sem esperar consulta
 * nenhuma. O catálogo cobre o serviço que ele acabou de acrescentar.
 */
function namesOf(
  ids: readonly string[],
  request: AppointmentView,
  catalog: readonly ServiceView[] | undefined,
): string[] {
  return ids.map(
    (id) =>
      request.services.find((service) => service.id === id)?.name ??
      catalog?.find((service) => service.id === id)?.name ??
      'serviço',
  );
}

/** Deslocamentos oferecidos em um toque — o ajuste real é quase sempre pequeno. */
const SHIFTS = [-30, -15, -5, 5, 15, 30];

export function RequestReviewSheet({ request, onClose }: Props) {
  const { tenant } = useTenant();
  const queryClient = useQueryClient();

  const [shiftMinutes, setShiftMinutes] = useState(0);
  const [durationMinutes, setDurationMinutes] = useState(request.durationMinutes);
  /** Ele mexeu no campo de minutos: daí em diante o número é dele. */
  const [durationTouched, setDurationTouched] = useState(false);
  const [serviceIds, setServiceIds] = useState(request.services.map((service) => service.id));
  const [editingServices, setEditingServices] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [rejecting, setRejecting] = useState(false);

  const date = request.startsAt.slice(0, 10);
  const startsAt = new Date(new Date(request.startsAt).getTime() + shiftMinutes * 60_000);

  const requestedIds = request.services.map((service) => service.id);
  const changedServices =
    serviceIds.length !== requestedIds.length ||
    serviceIds.some((id) => !requestedIds.includes(id));

  const changed =
    shiftMinutes !== 0 || durationMinutes !== request.durationMinutes || changedServices;

  const { data: agenda, isPending } = useQuery({
    queryKey: ['agenda', date, request.staff.id],
    queryFn: () =>
      api.get<DayAgendaView>('/appointments/agenda', { date, membershipId: request.staff.id }),
  });

  // O catálogo só é buscado quando ele abre a lista para mexer: quase todo
  // pedido é confirmado como veio.
  const { data: catalog } = useQuery({
    queryKey: ['services'],
    queryFn: () => api.get<{ services: ServiceView[] }>('/services'),
    enabled: editingServices,
  });

  /**
   * Quanto os serviços escolhidos levam **para este cliente**.
   *
   * Vem da disponibilidade porque é lá que essa conta mora: acrescentar a barba
   * não acrescenta os 20 minutos do cadastro, acrescenta os 30 que a barba
   * daquele cliente leva. Chutar o padrão aqui faria a tela mostrar um dia que
   * não é o que seria gravado.
   */
  const { data: recalculated } = useQuery({
    queryKey: ['availability', date, request.staff.id, serviceIds.join(','), request.client.id],
    queryFn: () =>
      api.get<{ days: Array<{ services: AvailabilityServiceView[] }> }>('/schedule/availability', {
        date,
        membershipId: request.staff.id,
        serviceIds: serviceIds.join(','),
        clientId: request.client.id,
      }),
    enabled: changedServices && serviceIds.length > 0,
  });

  const recalculatedServices = recalculated?.days[0]?.services;

  useEffect(() => {
    if (!recalculatedServices || durationTouched) return;
    setDurationMinutes(
      recalculatedServices.reduce((sum, service) => sum + service.durationMinutes, 0),
    );
  }, [recalculatedServices, durationTouched]);

  const respond = useMutation({
    mutationFn: (input: { decision: 'CONFIRM' | 'REJECT'; acceptConflict?: boolean }) =>
      api.post(`/appointments/${request.id}/respond`, {
        decision: input.decision,
        // Só manda o que mudou: sem ajuste, o horário pedido é mantido tal qual.
        startsAt:
          input.decision === 'CONFIRM' && shiftMinutes !== 0 ? startsAt.toISOString() : undefined,
        // O tempo só viaja quando ele digitou um: mudar a lista já recalcula o
        // tempo no servidor, com a ficha do cliente, que é a fonte certa.
        durationMinutes:
          input.decision === 'CONFIRM' && durationTouched ? durationMinutes : undefined,
        services:
          input.decision === 'CONFIRM' && changedServices
            ? serviceIds.map((serviceId) => ({ serviceId }))
            : undefined,
        note: note.trim() || undefined,
        acceptConflict: input.acceptConflict ?? false,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['pending'] });
      await queryClient.invalidateQueries({ queryKey: ['pending-count'] });
      await queryClient.invalidateQueries({ queryKey: ['agenda'] });
      onClose();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught : null),
  });

  // O dia inteiro, com o pedido colocado onde ele ficaria e a sobreposição de
  // cada vizinho já medida. É o que permite julgar o encaixe sem sair da tela.
  const chosenNames = namesOf(serviceIds, request, catalog?.services);
  const dayEntries = buildDay(
    agenda,
    request,
    startsAt,
    durationMinutes,
    tenant.timezone,
    chosenNames,
  );

  const tolerance = agenda?.overlapToleranceMinutes ?? 0;
  const overlapping = dayEntries.filter((entry) => !entry.isRequest && entry.overlapMinutes > 0);
  const worstOverlap = overlapping.reduce(
    (worst, entry) => Math.max(worst, entry.overlapMinutes),
    0,
  );

  /*
   * Fora do expediente também precisa aparecer aqui.
   *
   * Adiantar meia hora pode empurrar o atendimento para dentro do almoço sem
   * cruzar cliente nenhum — não há sobreposição, e mesmo assim o horário não
   * existe. Sem este aviso o barbeiro só descobriria ao confirmar e levar erro.
   */
  const outsideHours = isOutsideWorkingHours(agenda, startsAt, durationMinutes, tenant.timezone);

  const listRef = useRef<HTMLDivElement>(null);
  const requestRowRef = useRef<HTMLLIElement>(null);

  /*
   * Mantém o pedido centralizado na lista sempre que ele muda de posição.
   *
   * `scrollIntoView` rolaria a folha inteira junto e tiraria os botões do
   * alcance do polegar, então mexemos só no `scrollTop` do contêiner.
   *
   * A distância sai da diferença entre os retângulos, e não de `offsetTop`:
   * `offsetTop` é medido a partir do ancestral posicionado, que aqui é a folha
   * inteira — usá-lo jogava a lista para o fim do dia, com o pedido fora de
   * vista.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: as dependências são gatilhos, não leituras — o corpo só toca refs, mas precisa rodar de novo a cada mudança de horário, duração ou agenda
  useEffect(() => {
    const list = listRef.current;
    const row = requestRowRef.current;
    if (!list || !row) return;

    const listBox = list.getBoundingClientRect();
    const rowBox = row.getBoundingClientRect();

    list.scrollTop += rowBox.top - listBox.top - list.clientHeight / 2 + rowBox.height / 2;
  }, [startsAt.getTime(), durationMinutes, agenda]);

  return (
    <Sheet
      open
      onClose={onClose}
      title={request.client.name}
      footer={
        <div className="flex flex-col gap-2 pb-1">
          {error?.code === 'CONFLICT' && (
            <Button
              variant="danger"
              full
              onClick={() => respond.mutate({ decision: 'CONFIRM', acceptConflict: true })}
            >
              Encaixar mesmo assim
            </Button>
          )}

          {rejecting ? (
            <>
              <Button
                variant="danger"
                full
                loading={respond.isPending}
                onClick={() => respond.mutate({ decision: 'REJECT' })}
              >
                {note.trim() ? 'Recusar e avisar' : 'Recusar sem recado'}
              </Button>
              <Button variant="ghost" full onClick={() => setRejecting(false)}>
                Voltar
              </Button>
            </>
          ) : (
            <>
              <Button
                full
                loading={respond.isPending}
                onClick={() => respond.mutate({ decision: 'CONFIRM' })}
              >
                {changed
                  ? `Confirmar às ${timeOf(startsAt, tenant.timezone)}`
                  : 'Confirmar como pedido'}
              </Button>
              <Button variant="danger" full onClick={() => setRejecting(true)}>
                Recusar
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <section>
          <p className="text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
            O cliente pediu
          </p>
          <p className="pt-1 text-[17px] font-semibold text-ink">
            {longDateOf(date, tenant.timezone)} às{' '}
            <span className="tabular-nums">{timeOf(request.startsAt, tenant.timezone)}</span>
          </p>
          <p className="text-[14px] text-ink-soft">
            {describeServices(request.services.map((service) => service.name))} ·{' '}
            {duration(request.durationMinutes)}
          </p>
          {request.notes && <p className="pt-2 text-[14px] text-ink italic">“{request.notes}”</p>}
          {request.client.totalNoShows > 0 && (
            <p className="pt-2">
              <Badge tone="danger">
                {request.client.totalNoShows}{' '}
                {request.client.totalNoShows === 1 ? 'falta' : 'faltas'} no histórico
              </Badge>
            </p>
          )}
        </section>

        {/*
          Sobreposição em destaque, antes da lista.
          O barbeiro pode querer o encaixe — mas não pode descobrir que ele
          existe só depois de confirmar.

          Dentro da tolerância que a própria barbearia configurou, é informação;
          acima dela, é alerta. Tratar os dois igual faria o alerta virar ruído
          na primeira semana.
        */}
        {outsideHours && (
          <div className="rounded-xl border-2 border-warn bg-warn/15 px-4 py-3">
            <p className="text-[14px] font-bold text-ink">⚠ Fora do seu expediente</p>
            <p className="pt-1 text-[13px] text-ink-soft">
              Das {timeOf(startsAt, tenant.timezone)} às{' '}
              {timeOf(new Date(startsAt.getTime() + durationMinutes * 60_000), tenant.timezone)}{' '}
              você não atende neste dia — pode ser o intervalo ou depois de fechar.
            </p>
          </div>
        )}

        {worstOverlap > 0 && (
          <div
            className={`rounded-xl px-4 py-3 ${
              worstOverlap > tolerance
                ? 'border-2 border-warn bg-warn/15'
                : 'border border-line bg-surface-raised'
            }`}
          >
            <p
              className={`text-[14px] ${
                worstOverlap > tolerance ? 'font-bold text-ink' : 'font-medium text-ink-soft'
              }`}
            >
              {worstOverlap > tolerance
                ? `⚠ Este horário invade ${worstOverlap} min de outro atendimento`
                : `Encaixe de ${worstOverlap} min — dentro da sua tolerância de ${tolerance} min`}
            </p>
            <p className="pt-1 text-[13px] text-ink-soft">
              {overlapping
                .map((entry) => `${entry.label} (${entry.overlapMinutes} min)`)
                .join(', ')}
              {worstOverlap > tolerance && '. Ajuste abaixo ou confirme o encaixe mesmo assim.'}
            </p>
          </div>
        )}

        {/* O dia inteiro, rolável */}
        <section>
          <div className="flex items-baseline justify-between pb-2">
            <p className="text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
              Como fica o dia
            </p>
            <span className="text-[12px] text-ink-faint">
              {dayEntries.length > 1 ? `${dayEntries.length - 1} já marcados` : 'dia livre'}
            </span>
          </div>

          {isPending && <Spinner />}

          {!isPending && (
            <div
              ref={listRef}
              className="scroll-touch max-h-72 overflow-y-auto rounded-xl border border-line"
            >
              <ul>
                {dayEntries.map((entry) => (
                  <li
                    key={entry.key}
                    ref={entry.isRequest ? requestRowRef : undefined}
                    className={`flex items-center justify-between gap-3 border-b border-line px-4 py-2.5 last:border-b-0 ${
                      entry.isRequest
                        ? 'bg-primary/20 ring-1 ring-primary ring-inset'
                        : entry.overlapMinutes > tolerance
                          ? 'bg-warn/10'
                          : 'bg-surface-raised'
                    }`}
                  >
                    <span className="min-w-0">
                      <span
                        className={`block truncate text-[14px] ${
                          entry.isRequest ? 'font-semibold text-ink' : 'text-ink-soft'
                        }`}
                      >
                        {entry.label}
                      </span>
                      {entry.detail && (
                        <span className="block truncate text-[12px] text-ink-faint">
                          {entry.detail}
                        </span>
                      )}
                    </span>

                    <span className="shrink-0 text-right">
                      <span
                        className={`block text-[13px] tabular-nums ${
                          entry.isRequest ? 'font-bold text-ink' : 'text-ink-faint'
                        }`}
                      >
                        {entry.time}
                      </span>
                      {entry.overlapMinutes > 0 && (
                        <span
                          className={`block text-[11px] font-semibold ${
                            entry.overlapMinutes > tolerance ? 'text-warn' : 'text-ink-faint'
                          }`}
                        >
                          {entry.overlapMinutes > tolerance ? '⚠ ' : '↔ '}
                          {entry.overlapMinutes} min
                        </span>
                      )}
                    </span>
                  </li>
                ))}
                {dayEntries.length === 0 && (
                  <li className="bg-surface-raised px-4 py-3 text-[14px] text-ink-soft">
                    Nada mais marcado neste dia.
                  </li>
                )}
              </ul>
            </div>
          )}
        </section>

        {/* Ajustes */}
        <section className="flex flex-col gap-3 rounded-xl border border-line bg-surface-raised p-4">
          <div>
            <p className="text-[13px] text-ink-soft">Adiantar ou atrasar</p>
            <div className="flex flex-wrap gap-1.5 pt-2">
              {SHIFTS.map((minutes) => (
                <button
                  key={minutes}
                  type="button"
                  onClick={() => setShiftMinutes((current) => current + minutes)}
                  className="touch-target rounded-lg border border-line bg-surface px-3 text-[14px] font-semibold tabular-nums text-ink"
                >
                  {minutes > 0 ? `+${minutes}` : minutes}
                </button>
              ))}
              {shiftMinutes !== 0 && (
                <button
                  type="button"
                  onClick={() => setShiftMinutes(0)}
                  className="touch-target rounded-lg px-3 text-[13px] font-medium text-accent"
                >
                  desfazer
                </button>
              )}
            </div>
            {shiftMinutes !== 0 && (
              <p className="pt-2 text-[14px] font-medium text-ink">
                Novo horário: {timeOf(startsAt, tenant.timezone)}{' '}
                <span className="text-ink-faint">
                  ({shiftMinutes > 0 ? `${shiftMinutes} min depois` : `${-shiftMinutes} min antes`})
                </span>
              </p>
            )}
          </div>

          {/*
            "Hoje só dá para o cabelo" na hora de confirmar.
            Sai um aviso só para o cliente, com o que vai acontecer de verdade —
            e a tela dele destaca o que saiu do pedido.
          */}
          <div className="border-t border-line pt-3">
            <div className="flex items-baseline justify-between gap-2">
              <p className="min-w-0 text-[13px] text-ink-soft">
                O que vai ser feito:{' '}
                <span className="font-medium text-ink">{describeServices(chosenNames)}</span>
              </p>
              <button
                type="button"
                onClick={() => setEditingServices((current) => !current)}
                className="shrink-0 touch-target px-1 text-[13px] font-medium text-accent"
              >
                {editingServices ? 'pronto' : 'mudar'}
              </button>
            </div>

            {editingServices && (
              <div className="pt-2">
                <OptionList
                  items={catalog?.services.filter((entry) => entry.active) ?? []}
                  selected={serviceIds}
                  getKey={(entry) => entry.id}
                  onSelect={(entry) =>
                    setServiceIds((current) =>
                      current.includes(entry.id)
                        ? current.filter((id) => id !== entry.id)
                        : [...current, entry.id],
                    )
                  }
                  renderTitle={(entry) => entry.name}
                  renderMeta={(entry) => duration(entry.defaultDurationMinutes)}
                />
              </div>
            )}

            {changedServices && (
              <p className="pt-2 text-[13px] text-warn">
                O cliente pediu {describeServices(request.services.map((service) => service.name))}.
                Escreva um recado explicando.
              </p>
            )}
          </div>

          <div className="border-t border-line pt-3">
            <p className="text-[13px] text-ink-soft">
              Tempo do atendimento
              {durationMinutes !== request.durationMinutes && (
                <span className="text-ink-faint"> · pedido: {request.durationMinutes} min</span>
              )}
            </p>
            <div className="flex items-center gap-2 pt-2">
              <input
                type="number"
                inputMode="numeric"
                min={5}
                max={480}
                step={5}
                value={durationMinutes}
                onChange={(event) => {
                  setDurationTouched(true);
                  setDurationMinutes(Number(event.target.value));
                }}
                className="touch-target w-24 rounded-lg border border-line bg-surface px-3 py-2 text-[16px] tabular-nums text-ink"
              />
              <span className="text-[14px] text-ink-soft">minutos</span>
              <div className="ml-auto flex gap-1">
                {[-5, 5].map((step) => (
                  <button
                    key={step}
                    type="button"
                    onClick={() => {
                      setDurationTouched(true);
                      setDurationMinutes((current) => Math.min(480, Math.max(5, current + step)));
                    }}
                    className="touch-target w-11 rounded-lg border border-line bg-surface text-[17px] font-semibold text-ink-soft"
                  >
                    {step > 0 ? '+' : '−'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Recado */}
        <section className="flex flex-col gap-2">
          <label htmlFor="recado" className="text-[13px] font-medium text-ink-soft">
            Recado para o cliente{' '}
            {rejecting ? '(opcional)' : changed ? '(recomendado)' : '(opcional)'}
          </label>
          <textarea
            id="recado"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={3}
            maxLength={500}
            placeholder={
              rejecting
                ? 'Ex.: Nesse dia estou fechado, me chama que a gente acha outro horário'
                : changed
                  ? 'Ex.: Puxei 15 minutos porque tenho um compromisso depois'
                  : 'Ex.: Confirmado! Até lá'
            }
            className="w-full rounded-xl border border-line bg-surface-raised px-4 py-3 text-[16px] text-ink placeholder:text-ink-faint focus:border-primary focus:outline-none"
          />
          {changed && !note.trim() && (
            <p className="text-[13px] text-warn">
              Você mudou o pedido. Um recado explicando evita o cliente achar que errou o horário.
            </p>
          )}
        </section>

        {error && error.code !== 'CONFLICT' && <ErrorNote>{error.message}</ErrorNote>}
        {error?.code === 'CONFLICT' && (
          <div className="rounded-xl border border-warn/40 bg-warn/10 px-4 py-3 text-[14px] text-ink">
            {error.message}
          </div>
        )}
      </div>
    </Sheet>
  );
}

interface DayEntry {
  key: string;
  minutes: number;
  label: string;
  detail?: string;
  time: string;
  isRequest: boolean;
  /** Minutos em que este atendimento e o pedido se cruzam. */
  overlapMinutes: number;
}

/** Minutos em que dois intervalos se cruzam. Zero quando só se encostam. */
function overlapBetween(aStart: number, aEnd: number, bStart: number, bEnd: number): number {
  return Math.max(0, Math.min(aEnd, bEnd) - Math.max(aStart, bStart));
}

/**
 * O dia inteiro, com o pedido inserido na posição em que ficaria.
 *
 * A lista é completa e rolável de propósito: recortar dois antes e dois depois
 * escondia o resto do dia justamente de quem precisa decidir se cabe. E como o
 * pedido entra na ordenação pelo horário ajustado, arrastá-lo quinze minutos o
 * faz trocar de lugar na lista — que é a leitura que o barbeiro quer.
 */
function buildDay(
  agenda: DayAgendaView | undefined,
  request: AppointmentView,
  startsAt: Date,
  durationMinutes: number,
  timezone: string,
  serviceNames: readonly string[],
): DayEntry[] {
  if (!agenda) return [];

  const mineStart = minutesOfDay(startsAt, timezone);
  const mineEnd = mineStart + durationMinutes;
  const endsAt = new Date(startsAt.getTime() + durationMinutes * 60_000);

  const others = agenda.appointments
    .filter((appointment) => appointment.id !== request.id)
    .filter((appointment) => ['REQUESTED', 'CONFIRMED', 'IN_PROGRESS'].includes(appointment.status))
    .map((appointment) => {
      const start = minutesOfDay(appointment.startsAt, timezone);
      const end = start + appointment.durationMinutes;

      return {
        key: appointment.id,
        minutes: start,
        label: appointment.client.name,
        detail: `${shortServicesLabel(appointment.services.map((service) => service.name))} · ${duration(appointment.durationMinutes)}`,
        time: `${timeOf(appointment.startsAt, timezone)}–${timeOf(appointment.endsAt, timezone)}`,
        isRequest: false,
        overlapMinutes: overlapBetween(mineStart, mineEnd, start, end),
      };
    });

  const worst = others.reduce((max, entry) => Math.max(max, entry.overlapMinutes), 0);

  const mine: DayEntry = {
    key: request.id,
    minutes: mineStart,
    label: `${request.client.name} · este pedido`,
    detail: `${describeServices(serviceNames)} · ${duration(durationMinutes)}`,
    time: `${timeOf(startsAt, timezone)}–${timeOf(endsAt, timezone)}`,
    isRequest: true,
    overlapMinutes: worst,
  };

  return [...others, mine].sort((a, b) => a.minutes - b.minutes || a.time.localeCompare(b.time));
}

/**
 * O atendimento cabe inteiro dentro de alguma faixa de trabalho do dia?
 *
 * Um horário no intervalo de almoço não sobrepõe cliente nenhum e ainda assim
 * não existe. A API recusa na confirmação; a tela precisa dizer antes.
 */
function isOutsideWorkingHours(
  agenda: DayAgendaView | undefined,
  startsAt: Date,
  durationMinutes: number,
  timezone: string,
): boolean {
  if (!agenda || agenda.workingRanges.length === 0) return false;

  const start = minutesOfDay(startsAt, timezone);
  const end = start + durationMinutes;

  return !agenda.workingRanges.some(
    (range) => start >= parseHhMm(range.start) && end <= parseHhMm(range.end),
  );
}
